// SCR-CONSOLE-006 — درخواست‌های دسترسی پشتیبانی (US-PLT-081): همه درخواست‌ها و مجوزها (فقط فعال یا همه) با وضعیت و زمان پایان؛
// درخواست تازه از تب «عملیات» جزئیات مستأجر. تأیید اضطراری (بدون مالک مستأجر) فقط با platform.tenant.suspend و کاربری جز
// درخواست‌کننده (دو مدیر پلتفرم — BR-PLT-090، C-AGENT-421).
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { unwrap, type SupportAccessGrantView, type SupportAccessStatus } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  JalaliDate,
  ListPage,
  ReasonDialog,
  StatusChip,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { usePlatformAccess } from '../../access';
import { consoleKeys, toNumber } from '../../api';

export const SCR_CONSOLE_006 = 'SCR-CONSOLE-006';

function tone(status: SupportAccessStatus): StatusTone {
  switch (status) {
    case 'Approved':
      return 'active';
    case 'Requested':
      return 'pending';
    case 'Rejected':
    case 'Revoked':
      return 'no_show';
    default:
      return 'inactive';
  }
}

/** جدول مجوزهای دسترسی پشتیبانی (همه یا یک مستأجر). */
export function SupportGrantTable({
  tenantId,
  activeOnly = false,
}: {
  tenantId?: string;
  activeOnly?: boolean;
}) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const [approving, setApproving] = useState<SupportAccessGrantView | null>(null);

  const grants = useQuery({
    queryKey: [...consoleKeys.supportAccess(tenantId ?? null), activeOnly],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/platform/console/support-access', {
          params: { query: { tenantId, activeOnly } },
        }),
      ),
  });
  const emergency = useMutation({
    mutationFn: ({ grant, note }: { grant: SupportAccessGrantView; note: string }) =>
      unwrap(
        api.POST('/api/v1/platform/console/support-access/{grantId}:emergency-approve', {
          params: { path: { grantId: grant.id } },
          body: { includesClinical: grant.requestedClinical, note },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.supportAccessRoot });
      setApproving(null);
    },
  });

  const columns: DataTableColumn<SupportAccessGrantView>[] = [
    ...(tenantId
      ? []
      : [
          {
            id: 'tenant',
            headerKey: 'console:support_access.tenant',
            cell: (row: SupportAccessGrantView) => (
              <Link component={RouterLink} to={`/tenants/${row.tenantId}`}>
                <bdi dir="ltr">{row.tenantId.slice(0, 8)}</bdi>
              </Link>
            ),
          },
        ]),
    {
      id: 'requestedBy',
      headerKey: 'console:support_access.requested_by',
      cell: (row) => row.requestedByName,
    },
    { id: 'reason', headerKey: 'console:support_access.reason', cell: (row) => row.reason },
    {
      id: 'duration',
      headerKey: 'console:support_access.duration',
      cell: (row) => t('console:support_access.hours', { value: faDigits(toNumber(row.durationHours) ?? 0) }),
    },
    {
      id: 'flags',
      headerKey: 'console:support_access.scope',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {row.requestedClinical && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t('console:support_access.clinical_short')}
            />
          )}
          {row.isEmergency && (
            <Chip size="small" color="error" label={t('console:support_access.emergency_short')} />
          )}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'console:support_access.status_label',
      cell: (row) => (
        <StatusChip tone={tone(row.status)} labelKey={`console:support_access.status.${row.status}`} />
      ),
    },
    {
      id: 'expires',
      headerKey: 'console:support_access.expires_at',
      cell: (row) =>
        row.endedAt ? (
          <JalaliDate value={row.endedAt} withTime />
        ) : row.expiresAt ? (
          <JalaliDate value={row.expiresAt} withTime />
        ) : (
          t('console:support_access.not_started')
        ),
    },
    {
      id: 'actions',
      headerKey: 'console:support_access.actions',
      cell: (row) =>
        row.status === 'Requested' && row.isEmergency && access.can('platform.tenant.suspend') ? (
          <Button
            size="small"
            color="error"
            onClick={() => setApproving(row)}
            data-testid={`emergency-approve-${row.id}`}
          >
            {t('console:support_access.emergency_approve')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        captionKey="console:support_access.caption"
        columns={columns}
        rows={grants.data?.items}
        getRowId={(row) => row.id}
        isLoading={grants.isPending}
        error={grants.error}
        onRetry={() => void grants.refetch()}
        emptyTitleKey="console:support_access.empty"
      />
      <ReasonDialog
        open={approving !== null}
        titleKey="console:support_access.emergency_title"
        messageKey="console:support_access.emergency_message"
        confirmKey="console:support_access.emergency_approve"
        busy={emergency.isPending}
        error={emergency.error}
        onCancel={() => {
          emergency.reset();
          setApproving(null);
        }}
        onConfirm={(note) => approving && emergency.mutate({ grant: approving, note })}
      />
    </>
  );
}

export function SupportAccessPage() {
  const { t } = useTranslation('console');
  const [activeOnly, setActiveOnly] = useState(false);
  return (
    <ListPage
      screenId={SCR_CONSOLE_006}
      titleKey="console:support_access.title"
      subtitleKey="console:support_access.subtitle"
      filters={
        <FormControlLabel
          control={
            <Switch
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
              data-testid="support-active-only"
            />
          }
          label={t('console:support_access.active_only')}
        />
      }
    >
      <SupportGrantTable activeOnly={activeOnly} />
    </ListPage>
  );
}
